import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { products, companyInfo } from '../mock';

export const Products = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('Tất cả');

  const types = ['Tất cả', 'Gia đình', 'Combo', 'Cá nhân'];

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          product.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'Tất cả' || product.type === filterType;
    return matchesSearch && matchesType;
  });
  const hotlines = (companyInfo.hotlines?.length
    ? companyInfo.hotlines
    : [companyInfo.hotline, companyInfo.secondaryHotline]
  ).filter(Boolean);
  const primaryHotline = hotlines[0] || '';
  const supportingHotlines = hotlines.slice(1);
  const formatTelHref = (value = '') => `tel:${value.replace(/\s+/g, '')}`;

  const getImageSizeClass = (scale = 'medium') => {
    switch (scale) {
      case 'large':
        return 'max-h-[22rem] 3xl:max-h-[24rem] 4xl:max-h-[28rem]';
      case 'medium':
      default:
        return 'max-h-[16rem] 3xl:max-h-[18rem] 4xl:max-h-[20rem]';
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-white via-slate-50 to-slate-50">
      <Helmet>
        <title>Nước khoáng i-on kiềm | Sản phẩm</title>
        <meta
          name="description"
          content="Lọc và tìm kiếm các sản phẩm nước i-on kiềm O2 phù hợp nhu cầu gia đình, cá nhân hay doanh nghiệp với thông tin dung tích và tính năng chi tiết."
        />
        <link rel="canonical" href="https://ionkiemo2.vn/products" />
      </Helmet>
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 bg-gradient-to-br from-cyan-50 via-blue-50 to-white 3xl:pt-40 3xl:pb-24 3xl:px-8 4xl:pt-48 4xl:pb-32 4xl:px-12">
        <div className="container mx-auto text-center max-w-screen-4xl 3xl:px-12 4xl:px-16">
          <h1 className="text-5xl font-bold text-slate-900 mb-6 3xl:text-6xl 4xl:text-7xl">Sản phẩm của chúng tôi</h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed 3xl:text-2xl 4xl:text-[28px]">
            Đa dạng kích thước và dung tích, phù hợp với mọi nhu cầu sử dụng
          </p>
        </div>
      </section>

      {/* Filters Section */}
      <section className="py-8 px-4 bg-white border-b sticky top-20 z-40 3xl:py-10 3xl:px-8 4xl:py-12 4xl:px-12">
        <div className="container mx-auto max-w-screen-4xl 3xl:px-12 4xl:px-16">
          <div className="flex flex-col lg:flex-row gap-4 justify-between items-center w-full 3xl:gap-6">
            {/* Search */}
            <div className="relative w-full lg:w-96 3xl:w-[32rem]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400 4xl:h-6 4xl:w-6" />
              <Input
                type="text"
                placeholder="Tìm kiếm sản phẩm..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-12 text-base 4xl:h-14 4xl:text-lg"
              />
            </div>

            {/* Type Filters */}
            <div className="flex items-center gap-2 flex-wrap justify-center">
              {types.map((type) => (
                <Button
                  key={type}
                  variant={filterType === type ? 'default' : 'outline'}
                  onClick={() => setFilterType(type)}
                  className={`${filterType === type ? 'bg-cyan-600 hover:bg-cyan-700 text-white' : 'hover:bg-slate-100 text-slate-700'} text-sm px-4 py-2 3xl:text-base 3xl:px-6`}
                >
                  {type}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 px-4 bg-slate-50 flex-1 3xl:py-20 3xl:px-8 4xl:py-24 4xl:px-12">
        <div className="container mx-auto h-full flex flex-col max-w-screen-4xl 3xl:px-12 4xl:px-16">
          {filteredProducts.length === 0 ? (
            <div className="flex-1 flex items-center justify-center text-center">
              <p className="text-xl text-slate-600 3xl:text-2xl">Không tìm thấy sản phẩm nào</p>
            </div>
          ) : (
            <div className="flex-1">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 h-full max-w-6xl w-full mx-auto 3xl:gap-10 4xl:gap-12">
                {filteredProducts.map((product) => (
                  <Card key={product.id} className="overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300 group flex flex-col">
                    <div className="relative overflow-hidden bg-gradient-to-br from-cyan-50 to-blue-50 flex items-center justify-center h-80 3xl:h-96 4xl:h-[26rem]">
                      <img 
                        src={product.image} 
                        alt={product.name} 
                        className={`w-auto object-contain transition-transform duration-300 group-hover:scale-110 ${getImageSizeClass(product.imageScale)}`}
                      />
                      <div className="absolute top-4 right-4 bg-cyan-600 text-white px-3 py-1 rounded-full text-sm font-medium 3xl:px-4 3xl:py-2 3xl:text-base">
                        {product.type}
                      </div>
                    </div>
                    <CardContent className="p-6 flex flex-col flex-1 3xl:p-8 4xl:p-10">
                      <h3 className="text-xl font-bold text-slate-900 mb-2 3xl:text-2xl">{product.name}</h3>
                      <p className="text-slate-600 mb-4 text-base 3xl:text-lg">{product.description}</p>
                      <div className="mb-4 flex-grow">
                        <p className="text-sm font-medium text-cyan-600 mb-2 3xl:text-base">Dung tích: {product.capacity}</p>
                        <div className="space-y-1">
                          {product.features.map((feature, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-sm text-slate-600 3xl:text-base">
                              <div className="h-1.5 w-1.5 rounded-full bg-cyan-600"></div>
                              <span>{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <a href={formatTelHref(primaryHotline)}>
                        <Button className="w-full bg-cyan-600 hover:bg-cyan-700 text-white mt-auto 3xl:text-lg 4xl:h-14">
                          Gọi đặt hàng
                        </Button>
                      </a>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
